import React, { useState } from 'react';
import {
  LEGAL_DOCUMENT_VERSION,
  LEGAL_DOCUMENTS,
  LEGAL_ACCEPTANCE_SUMMARY,
} from '../../constants/legal';
import { BadgeIcon, CheckCircleIcon } from '../icons';
import { SidebarFedexporMark } from '../ui/Logo';
import LegalDocumentContent from './LegalDocumentContent';

const LegalConsentGate = ({ currentUser, onAccept, onLogout }) => {
  const [activeDoc, setActiveDoc] = useState('privacy');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const canSubmit = acceptedTerms && acceptedPrivacy && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError('');
    try {
      await onAccept({
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      });
    } catch (acceptError) {
      setError(acceptError.message || 'No se pudo registrar la aceptación.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-[100svh] bg-[#F6F8FB] text-slate-900">
      <div className="mx-auto grid min-h-[100svh] w-full max-w-7xl grid-cols-1 lg:grid-cols-[390px_1fr]">
        <aside className="flex flex-col border-b border-slate-200 bg-[#071326] px-5 py-6 text-white sm:px-8 lg:border-b-0 lg:border-r lg:border-white/10 lg:px-8 lg:py-8">
          <div className="flex items-start justify-between gap-4">
            <SidebarFedexporMark />
            <button
              type="button"
              onClick={onLogout}
              className="rounded-xl border border-white/10 px-3 py-2 text-xs font-semibold text-white/55 transition-colors hover:bg-white/10 hover:text-white"
            >
              Salir
            </button>
          </div>

          <div className="mt-10 space-y-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#004AAD] text-white">
              <BadgeIcon className="h-5 w-5" />
            </div>
            <div>
              <p className="text-[0.62rem] font-bold uppercase tracking-[0.16em] text-[#76A7E8]">
                Requisito de acceso
              </p>
              <h1 className="mt-2 text-2xl font-bold leading-tight">
                Aceptación de términos y protección de datos
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-white/58">
                Para continuar, {currentUser?.name || 'tu usuario'} debe aceptar la versión vigente de los documentos legales de la plataforma.
              </p>
            </div>
          </div>

          <div className="mt-8 rounded-xl border border-white/10 bg-white/[0.05] p-4">
            <p className="text-[0.62rem] font-bold uppercase tracking-[0.16em] text-white/35">
              Se guardará
            </p>
            <div className="mt-3 space-y-2.5 text-sm text-white/66">
              <p>Versión: {LEGAL_DOCUMENT_VERSION}</p>
              <p>Usuario: {currentUser?.email || currentUser?.contactEmail || currentUser?.id}</p>
              <p>Fecha: timestamp de servidor</p>
            </div>
          </div>
        </aside>

        <main className="flex min-h-0 flex-col px-4 py-5 sm:px-6 lg:h-screen lg:px-8 lg:py-8">
          <div className="mb-4 flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
              {[
                ['privacy', LEGAL_DOCUMENTS.privacy.shortTitle],
                ['terms', LEGAL_DOCUMENTS.terms.shortTitle],
              ].map(([type, label]) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setActiveDoc(type)}
                  className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                    activeDoc === type ? 'bg-[#004AAD] text-white' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-400">
              Versión vigente: {LEGAL_DOCUMENT_VERSION}
            </p>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
            <LegalDocumentContent type={activeDoc} dense />
          </div>

          <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="grid gap-3 lg:grid-cols-[1fr_330px] lg:items-end">
              <div className="space-y-3">
                {LEGAL_ACCEPTANCE_SUMMARY.slice(0, 2).map((item, index) => (
                  <label key={item} className="flex items-start gap-3 text-sm leading-relaxed text-slate-600">
                    <input
                      type="checkbox"
                      checked={index === 0 ? acceptedTerms : acceptedPrivacy}
                      onChange={(event) => {
                        if (index === 0) setAcceptedTerms(event.target.checked);
                        else setAcceptedPrivacy(event.target.checked);
                      }}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-[#004AAD] focus:ring-[#004AAD]"
                    />
                    <span>{item}</span>
                  </label>
                ))}
                <div className="space-y-1.5 pl-7">
                  {LEGAL_ACCEPTANCE_SUMMARY.slice(2).map((item) => (
                    <p key={item} className="text-xs leading-relaxed text-slate-400">
                      {item}
                    </p>
                  ))}
                </div>
                {error && <p className="text-sm font-medium text-rose-600">{error}</p>}
              </div>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={`flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold text-white transition-all ${
                  canSubmit ? 'bg-[#004AAD] shadow-sm hover:bg-[#003780]' : 'cursor-not-allowed bg-slate-300'
                }`}
              >
                <CheckCircleIcon className="h-4 w-4" />
                {isSubmitting ? 'Registrando...' : 'Aceptar y continuar'}
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default LegalConsentGate;
